import React, {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from "react";
import { Input } from "@ndd/ui";
import {
  RD_SCHEDULE_BOOKING_EMAIL_NOTE,
  RD_SCHEDULE_REPEAT_MAX,
  RD_SCHEDULE_REPEAT_MIN,
  type RdScheduleActionRequestBody,
  type RdScheduleAppointmentType,
  type RdScheduleRepeatInterval,
  type RdSchedulePreviewResponse,
} from "../../../../../shared/rd-schedule-actions";
import {
  REPEAT_LABELS,
  bookConfirmLabel,
  fetchPreview,
  localParts,
  repeatPreviewLine,
  shortDateLabel,
  type ScheduleApi,
} from "../../../lib/rd-schedule-actions";
import type { ActionClient, ActionDialogContext } from "./action-types";
import { ActionDialogFrame, ActionForm } from "./action-frame";
import { EmailCheckbox, Field, TimePicker, TypeSelect } from "./action-parts";
import { ClientSearch } from "./client-search";
import { useAppointmentTypes } from "./use-appointment-types";
import { useActionSubmit, useScheduleApi } from "./use-action-submit";
import { useTimePick, type TimePick } from "./use-time-pick";
import { RepeatSelect } from "./repeat-select";

// NO-876. New appointment (spec § Appointment actions; canvas New.dc.html):
// Client search, type, date, open times, repeats with a preview line, and an
// email for the details. Every visit is video. The confirm button names the
// result ("Book 6 visits").

export interface NewAppointmentDialogProps extends ActionDialogContext {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
  /** Set by Book next visit: the Client is already chosen. */
  client?: ActionClient;
}

export function NewAppointmentDialog(props: NewAppointmentDialogProps): ReactElement {
  const [pending, setPending] = useState(false);
  const listOpen = useRef(false);

  return (
    <ActionDialogFrame
      open={props.open}
      onOpenChange={props.onOpenChange}
      pending={pending}
      holdEscape={() => listOpen.current}
      title="New appointment"
      subtitle="All visits are video. The link goes out with the confirmation."
    >
      <NewForm {...props} onPending={setPending} listOpenRef={listOpen} />
    </ActionDialogFrame>
  );
}

export interface NewDraft {
  client: ActionClient | null;
  typeId: string | null;
  date: string;
  repeat: RdScheduleRepeatInterval | "none";
  count: number;
  email: boolean;
}

const DEFAULT_COUNT = 6;

function todayIn(timeZone: string): string {
  return localParts(new Date().toISOString(), timeZone).date;
}

function repeatOf(
  draft: NewDraft
): { interval: RdScheduleRepeatInterval; count: number } | null {
  return draft.repeat === "none"
    ? null
    : { interval: draft.repeat, count: draft.count };
}

export function isValidCount(count: number): boolean {
  return (
    Number.isInteger(count) &&
    count >= RD_SCHEDULE_REPEAT_MIN &&
    count <= RD_SCHEDULE_REPEAT_MAX
  );
}

export function visitCountOf(draft: NewDraft): number {
  return draft.repeat === "none" ? 1 : draft.count;
}

export function buildBookBody(
  draft: NewDraft,
  pick: Pick<TimePick, "startsAt" | "outside">
): RdScheduleActionRequestBody | null {
  if (!draft.client || !draft.typeId || !pick.startsAt) return null;

  return {
    kind: "book",
    patientId: draft.client.patientId,
    appointmentTypeId: draft.typeId,
    startsAt: pick.startsAt,
    outsideHours: pick.outside,
    repeat: repeatOf(draft),
    emailClient: draft.email,
  };
}

export type PreviewState =
  | { status: "none" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: RdSchedulePreviewResponse["data"] };

type PreviewRequest = Parameters<typeof fetchPreview>[1];

function previewRequest(
  rdId: string | null,
  draft: NewDraft,
  pick: Pick<TimePick, "startsAt" | "outside">
): PreviewRequest | null {
  const repeat = repeatOf(draft);

  if (!repeat || !isValidCount(repeat.count)) return null;

  if (!pick.startsAt || !draft.typeId) return null;

  return {
    rdId,
    appointmentTypeId: draft.typeId,
    startsAt: pick.startsAt,
    outsideHours: pick.outside,
    repeat,
  };
}

function useBookingPreview(input: {
  api: ScheduleApi;
  rdId: string | null;
  draft: NewDraft;
  pick: TimePick;
}): PreviewState {
  const { api, rdId, draft, pick } = input;
  const [state, setState] = useState<PreviewState>({ status: "none" });
  const request = previewRequest(rdId, draft, pick);
  const key = request ? JSON.stringify(request) : null;

  useEffect(() => {
    if (key === null) {
      setState({ status: "none" });

      return undefined;
    }

    let current = true;
    setState({ status: "loading" });

    fetchPreview(api, JSON.parse(key) as PreviewRequest)
      .then((response) => {
        if (current) setState({ status: "ready", data: response.data });
      })
      .catch(() => {
        if (current) setState({ status: "error" });
      });

    return () => {
      current = false;
    };
  }, [api, key]);

  return state;
}

function isBlocked(
  body: RdScheduleActionRequestBody | null,
  draft: NewDraft,
  preview: PreviewState
): boolean {
  if (body === null) return true;

  if (draft.repeat === "none") return false;

  return !isValidCount(draft.count) || !previewAllows(preview);
}

function useNewDraft(timeZone: string, client?: ActionClient) {
  const [draft, setDraft] = useState<NewDraft>(() => ({
    client: client ?? null,
    typeId: null,
    date: todayIn(timeZone),
    repeat: "none",
    count: DEFAULT_COUNT,
    email: true,
  }));

  return { draft, setDraft };
}

function useBookAction(
  input: Pick<
    NewAppointmentDialogProps,
    "rdId" | "timeZone" | "onChanged" | "onOpenChange"
  > & {
    api: ScheduleApi;
  },
  reloadTimes: () => void
) {
  return useActionSubmit({
    api: input.api,
    rdId: input.rdId,
    timeZone: input.timeZone,
    onSucceeded: () => {
      input.onChanged();
      input.onOpenChange(false);
    },
    onSlotConflict: reloadTimes,
    onPartial: input.onChanged,
  });
}

/** An edit to the draft, which also clears the problem left by the last try. */
function useDraftChange(
  clearProblem: () => void,
  setDraft: Dispatch<SetStateAction<NewDraft>>
): (patch: Partial<NewDraft>) => void {
  return (patch) => {
    clearProblem();
    setDraft((current) => ({ ...current, ...patch }));
  };
}

function NewForm(
  props: NewAppointmentDialogProps & {
    onPending: (pending: boolean) => void;
    listOpenRef: { current: boolean };
  }
): ReactElement {
  const { rdId, timeZone, onChanged, onOpenChange, onPending } = props;
  const api = useScheduleApi();
  const { types, failed: typesFailed } = useAppointmentTypes(api, rdId);
  const { draft, setDraft } = useNewDraft(timeZone, props.client);
  const typeId = draft.typeId ?? defaultTypeId(types);
  const type = types?.find((candidate) => candidate.id === typeId) ?? null;
  const pick = useTimePick({
    api,
    rdId,
    date: draft.date,
    appointmentTypeId: typeId,
    fallbackTimeZone: timeZone,
  });
  const action = useBookAction(
    { api, rdId, timeZone, onChanged, onOpenChange },
    pick.reload
  );
  const resolved = { ...draft, typeId };
  const preview = useBookingPreview({ api, rdId, draft: resolved, pick });
  const body = buildBookBody(resolved, pick);

  const change = useDraftChange(action.clearProblem, setDraft);

  useEffect(() => onPending(action.pending), [onPending, action.pending]);

  return (
    <ActionForm
      onSubmit={() => body && void action.submit(body)}
      problem={action.problem}
      back={{ label: "Close", onClick: () => onOpenChange(false) }}
      confirm={{
        label: bookConfirmLabel(visitCountOf(draft)),
        ...action.confirmState,
        disabled: isBlocked(body, draft, preview),
      }}
    >
      <NewFields
        {...{
          api,
          rdId,
          timeZone,
          draft,
          change,
          pick,
          preview,
          types,
          typeId,
          typesFailed,
        }}
        listOpenRef={props.listOpenRef}
        durationMinutes={type?.durationMinutes ?? null}
        disabled={action.pending || action.locked}
      />
    </ActionForm>
  );
}

function defaultTypeId(types: RdScheduleAppointmentType[] | null): string | null {
  if (!types || types.length === 0) return null;

  return (types.find((type) => !type.isInitial) ?? types[0]).id;
}

export function previewAllows(preview: PreviewState): boolean {
  return preview.status === "ready" && preview.data.allOpen;
}

/** The weeks that cannot be booked, so the RD sees them before booking. */
export function unavailableDates(preview: PreviewState, outside: boolean): string[] {
  if (preview.status !== "ready") return [];

  return preview.data.occurrences
    .filter((occurrence) => occurrence.status !== "open")
    .filter((occurrence) => !(outside && occurrence.status === "outside_hours"))
    .map((occurrence) => occurrence.localDate);
}

function PreviewLine({
  preview,
  outside,
  clientName,
}: {
  preview: PreviewState;
  outside: boolean;
  clientName: string;
}): ReactElement | null {
  if (preview.status === "none") return null;

  if (preview.status === "loading") {
    return <p className="-mt-2 text-body text-muted-foreground">Checking each week…</p>;
  }

  if (preview.status === "error") {
    return (
      <p role="alert" className="-mt-2 text-body text-danger">
        The weeks could not be checked. Change the repeat or try again.
      </p>
    );
  }

  const closed = unavailableDates(preview, outside);

  return (
    <div className="-mt-2 flex flex-col gap-1 text-body text-muted-strong">
      <p>{repeatPreviewLine(preview.data)}</p>
      {closed.length > 0 ? (
        <p role="alert" className="text-danger">
          {`${clientName || "The Client"} can't be booked on ${closed.map((date) => shortDateLabel(date)).join(", ")}. Pick another time or fewer visits.`}
        </p>
      ) : null}
    </div>
  );
}

function ClientAndType(props: {
  api: ScheduleApi;
  rdId: string | null;
  timeZone: string;
  draft: NewDraft;
  change: (patch: Partial<NewDraft>) => void;
  types: RdScheduleAppointmentType[] | null;
  typeId: string | null;
  typesFailed: boolean;
  listOpenRef: { current: boolean };
  disabled: boolean;
}): ReactElement {
  const { api, rdId, timeZone, draft, change, types, typeId, typesFailed, disabled } =
    props;
  const typeHint = typesFailed
    ? "Appointment types did not load. Close this and open New appointment again."
    : types?.length === 0
      ? "No video types are available."
      : undefined;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[3fr_2fr]">
      <ClientSearch
        {...{ api, rdId, timeZone, disabled }}
        value={draft.client}
        listOpenRef={props.listOpenRef}
        onChange={(client) => change({ client })}
      />
      <Field id="new-type" label="Appointment type" hint={typeHint}>
        <TypeSelect
          id="new-type"
          hasHint={typeHint !== undefined}
          value={typeId}
          types={types ?? []}
          disabled={disabled || !types?.length}
          onChange={(next) => change({ typeId: next })}
        />
      </Field>
    </div>
  );
}

function emailLabel(name: string): string {
  return name
    ? `Email ${name} the details and video link`
    : "Email the Client the details and video link";
}

export function NewFields(props: {
  api: ScheduleApi;
  rdId: string | null;
  timeZone: string;
  draft: NewDraft;
  change: (patch: Partial<NewDraft>) => void;
  pick: TimePick;
  preview: PreviewState;
  types: RdScheduleAppointmentType[] | null;
  typeId: string | null;
  typesFailed: boolean;
  listOpenRef: { current: boolean };
  durationMinutes: number | null;
  disabled: boolean;
}): ReactElement {
  const { timeZone, draft, change, pick, preview, disabled } = props;
  const name = draft.client?.displayName ?? "";

  return (
    <>
      <ClientAndType {...props} />
      <TimePicker
        idPrefix="new"
        dateLabel="Date"
        date={draft.date}
        minDate={todayIn(timeZone)}
        onDateChange={(date) => change({ date })}
        pick={pick}
        durationMinutes={props.durationMinutes}
        disabled={disabled}
      />
      <RepeatFields draft={draft} change={change} disabled={disabled} />
      <PreviewLine preview={preview} outside={pick.outside} clientName={name} />
      <EmailCheckbox
        id="new-email"
        label={emailLabel(name)}
        checked={draft.email}
        disabled={disabled}
        note={RD_SCHEDULE_BOOKING_EMAIL_NOTE}
        onChange={(email) => change({ email })}
      />
    </>
  );
}

function RepeatFields(props: {
  draft: NewDraft;
  change: (patch: Partial<NewDraft>) => void;
  disabled: boolean;
}): ReactElement {
  const { draft, change, disabled } = props;
  const countInvalid = draft.repeat !== "none" && !isValidCount(draft.count);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field id="new-repeat" label="Repeats">
        <RepeatSelect
          id="new-repeat"
          value={draft.repeat}
          labels={REPEAT_LABELS}
          disabled={disabled}
          onChange={(repeat) => change({ repeat })}
        />
      </Field>
      {draft.repeat !== "none" ? (
        <Field
          id="new-count"
          label="Number of visits"
          hint={
            countInvalid
              ? `Choose ${RD_SCHEDULE_REPEAT_MIN} to ${RD_SCHEDULE_REPEAT_MAX} visits.`
              : undefined
          }
        >
          <Input
            id="new-count"
            type="number"
            min={RD_SCHEDULE_REPEAT_MIN}
            max={RD_SCHEDULE_REPEAT_MAX}
            value={Number.isNaN(draft.count) ? "" : draft.count}
            disabled={disabled}
            aria-invalid={countInvalid}
            aria-describedby="new-count-hint"
            onChange={(event) => change({ count: event.target.valueAsNumber })}
          />
        </Field>
      ) : null}
    </div>
  );
}
